import { useMutation } from '@apollo/client';
import type { WorkflowCompletion } from '@generated/graphql';
import { WorkflowCompletionVisual } from '@ovComponents/4-module/workflowCompletion/workflowCompletion.visual';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { PageObjectType } from '@pages/setting/PageConfigurationDetail/pageConfigurationTypes';
import {
  updateNextWorkflowStep,
  updatePreviousWorkflowStep,
} from '@utils/hardCodedWorkflow';
import { useEffect, useState } from 'react';
import { GO_TO_STEP } from '../queries';

type GoToSubStepMutationData = {
  goToSubStep?: {
    workflowCompletion: WorkflowCompletion;
  };
};

export const WorkflowEditFlow = ({
  open,
  setOpen,
  objectId,
  objectType,
  refetch,
  targetWorkflowComplation,
  targetSubStepId,
  targetStepId,
  reviewStepId,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  objectId: string;
  objectType: PageObjectType;
  refetch: () => void;
  targetWorkflowComplation: any;
  targetSubStepId?: string;
  targetStepId?: string;
  reviewStepId?: string;
}) => {
  const targetStep = targetWorkflowComplation?.workflow?.steps.find(
    (step: any) => step?.id === targetStepId,
  );
  const targetSubStep = targetStep?.subSteps.find(
    (subStep: any) => subStep?.id === targetSubStepId,
  );

  const initialWorkflowCompletion = {
    id: targetWorkflowComplation?.id,
    currentStep: targetStep ?? targetWorkflowComplation?.currentStep,
    currentSubStep: targetSubStep ?? targetWorkflowComplation?.currentSubStep,
    workflow: targetWorkflowComplation?.workflow ?? undefined,
    context: targetWorkflowComplation?.context ?? {},
  };
  const [workflowData, setWorkflowData] = useState<any>(
    targetWorkflowComplation?.context ?? {},
  );
  const pageObjectType = [
    PageObjectType.INDIVIDUAL,
    PageObjectType.NON_INDIVIDUAL,
  ].includes(objectType)
    ? 'USER'
    : objectType;
  const [workflowCompletion, setWorkflowCompletion] = useState<any>({
    objectId,
    objectType: pageObjectType,
    ...initialWorkflowCompletion,
  });
  const [goToStep, { loading: goToStepLoading }] = useMutation(GO_TO_STEP());

  useEffect(() => {
    if (!open || !targetWorkflowComplation?.id || !targetSubStepId) return;

    let cancelled = false;
    void (async () => {
      const { data } = await goToStep({
        variables: {
          input: {
            workflowCompletionId: targetWorkflowComplation.id,
            subStepId: targetSubStepId,
          },
        },
      });
      if (cancelled) return;
      const typed = data as GoToSubStepMutationData | undefined;
      const nextWorkflowCompletion =
        typed?.goToSubStep?.workflowCompletion ?? undefined;
      if (!nextWorkflowCompletion) return;
      setWorkflowCompletion(nextWorkflowCompletion);
      setWorkflowData(nextWorkflowCompletion.context ?? {});
    })();

    return () => {
      cancelled = true;
    };
  }, [goToStep, open, targetSubStepId, targetWorkflowComplation?.id]);

  const updateNext = () => {
    updateNextWorkflowStep(workflowCompletion, setWorkflowCompletion, () => {
      refetch();
      setWorkflowData(targetWorkflowComplation?.context ?? {});
      setOpen(false);
      setWorkflowCompletion({
        objectId,
        objectType: pageObjectType,
        ...initialWorkflowCompletion,
      });
    });
  };

  const updatePrevious = () => {
    updatePreviousWorkflowStep(workflowCompletion, setWorkflowCompletion);
  };

  return (
    <WorkflowContext.Provider
      value={{
        workflowData,
        setWorkflowData,
      }}
    >
      <WorkflowCompletionVisual
        workflowCompletion={workflowCompletion}
        open={open}
        onClose={async () => {
          refetch();
          setWorkflowData(targetWorkflowComplation?.context ?? {});
          setOpen(false);
          setWorkflowCompletion({
            objectId,
            objectType: pageObjectType,
            ...initialWorkflowCompletion,
          });
        }}
        onNext={updateNext}
        previousStep={updatePrevious}
        loading={goToStepLoading}
        isInnerWorkflow={true}
        filterReviewStepId={reviewStepId}
      />
    </WorkflowContext.Provider>
  );
};
